import { Alert, Group, NumberInput, Popover } from "@mantine/core";
import { ICapitalLetter } from "@schemas/otherAttributesSchema";
import { IconAlertCircle, IconListNumbers } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { openapi, usePutProjectByProjectIdIssueUpdateFormulaCounterMutation } from "../../../../../../../../openapi";
import { useAppDispatch, useAppSelector } from "../../../../../../../hooks";
import { selectCurrentOpenIssueBoardInModal, selectCurrentOpenIssueInModal } from "../../../../issueSelectors";
import { selectFormulaCounterGroupIssuesBaseArgsInModal } from "./issueFormulaCounterSuggestion.selectors";
import IssueFormulaCounterGroupIssuesButton from "./IssueFormulaCounterGroupIssuesModal";
import IssueFormulaCounterSuggestions from "./IssueFormulaCounterSuggestions";

const getCounterErrorMessage = (error: unknown) => {
  const message =
    (error as { data?: { message?: string } })?.data?.message ?? (error as { message?: string })?.message ?? "";

  if (message.includes("already exists")) {
    return "Löpnummeret finnes allerede i samme formelgruppe. Velg et annet nummer.";
  }

  return "Kunne ikke lagre løpnummeret. Prøv igjen eller velg et annet nummer.";
};

const IssueFormulaCounterInput = ({ formulaAttributeKey }: { formulaAttributeKey: ICapitalLetter }) => {
  const dispatch = useAppDispatch();
  const issue = useAppSelector(selectCurrentOpenIssueInModal);
  const issueBoard = useAppSelector(selectCurrentOpenIssueBoardInModal);
  const baseArgs = useAppSelector(selectFormulaCounterGroupIssuesBaseArgsInModal);
  const formula = issueBoard?.otherAttributesConfig?.[formulaAttributeKey]?.formula;
  const [focused, setFocused] = useState(false);
  const [localValue, setLocalValue] = useState<number | "">("");
  const [counterError, setCounterError] = useState<string | null>(null);
  const [updateCounter, { isLoading: isSaving }] = usePutProjectByProjectIdIssueUpdateFormulaCounterMutation();

  const currentValue = issue?.formulaCounters?.[formulaAttributeKey]?.value ?? null;

  useEffect(() => {
    setLocalValue(currentValue ?? "");
  }, [currentValue]);

  if (issue == null || formula == null) return null;

  const saveValue = async (value: number | "") => {
    const counterValue = value === "" ? null : value;
    if (baseArgs == null || counterValue === currentValue) return false;
    const apiValue = counterValue ?? 0;

    try {
      const updatedIssue = await updateCounter({
        projectId: baseArgs.projectId,
        body: {
          issueId: issue._id,
          formulaAttributeKey,
          value: apiValue,
        },
      }).unwrap();

      dispatch(
        openapi.util.updateQueryData(
          "getProjectByProjectIdIssuesGetSingle",
          {
            projectId: baseArgs.projectId,
            issueId: issue._id,
          },
          () => updatedIssue,
        ),
      );

      setCounterError(null);
      return true;
    } catch (error) {
      setLocalValue(currentValue ?? "");
      setCounterError(getCounterErrorMessage(error));
      return false;
    }
  };

  const useSuggestion = async (suggestion: number) => {
    setLocalValue(suggestion);
    const saved = await saveValue(suggestion);
    if (saved) setFocused(false);
  };

  return (
    <>
      <Popover opened={focused} position="bottom-start" shadow="md" width="target" withArrow>
        <Popover.Target>
          <Group align="flex-end" gap="xs" wrap="nowrap">
            <NumberInput
              label="Löpnummer"
              min={1}
              allowDecimal={false}
              allowNegative={false}
              value={localValue}
              disabled={isSaving}
              leftSection={<IconListNumbers size={16} color="var(--mantine-color-orange-7)" />}
              style={{ flex: 1 }}
              onChange={(value) => {
                setCounterError(null);
                setLocalValue(typeof value === "number" ? value : "");
              }}
              onFocus={() => {
                setCounterError(null);
                setFocused(true);
              }}
              onBlur={() => {
                setFocused(false);
                saveValue(localValue);
              }}
            />
            <IssueFormulaCounterGroupIssuesButton formulaAttributeKey={formulaAttributeKey} />
          </Group>
        </Popover.Target>
        <Popover.Dropdown>
          <IssueFormulaCounterSuggestions
            formulaAttributeKey={formulaAttributeKey}
            isActive={focused}
            onSelectSuggestion={useSuggestion}
          />
        </Popover.Dropdown>
      </Popover>
      {counterError != null && (
        <Alert mt="xs" color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
          {counterError}
        </Alert>
      )}
    </>
  );
};

export default IssueFormulaCounterInput;
